import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, ArrowRight, Search } from 'lucide-react';
import SEO from '@/components/SEO';
import { Input } from '@/components/ui/input';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface PlatformBlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  meta_description: string | null;
  featured_image_url: string | null;
  word_count: number | null;
  reading_time: number | null;
  primary_keyword: string | null;
  secondary_keywords: string[] | null;
  published_at: string;
  created_at: string;
}

const Blog = () => {
  const [posts, setPosts] = useState<PlatformBlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Only select needed fields for better performance
      const { data, error: queryError } = await supabase
        .from('platform_blog_posts')
        .select('id, title, slug, excerpt, meta_description, featured_image_url, word_count, reading_time, primary_keyword, secondary_keywords, published_at, created_at')
        .eq('status', 'published')
        .order('published_at', { ascending: false })
        .limit(50); // Limit to improve performance

      if (queryError) {
        console.error('Error loading blog posts:', queryError);
        setError(`Failed to load blog posts: ${queryError.message}`);
        setPosts([]);
        return;
      }
      
      setPosts(data || []);
    } catch (err: any) {
      console.error('Error loading blog posts:', err);
      setError(err?.message || 'Failed to load blog posts. Please try again later.');
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts = posts.filter(post => {
    // Search filter
    const matchesSearch = 
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.primary_keyword?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.secondary_keywords?.some(kw => kw.toLowerCase().includes(searchQuery.toLowerCase()));
    
    return matchesSearch;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO
        title="Blog - SEO Tips, Guides & Insights | Seoro"
        description="Discover expert SEO tips, content marketing strategies, and insights to grow your organic traffic. Learn from our comprehensive guides and stay updated with the latest SEO trends."
        keywords="SEO blog, SEO tips, content marketing, SEO guides, organic traffic, SEO strategies, digital marketing"
        type="website"
      />
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/10 via-primary/5 to-background border-b border-border/50">
          <div className="container mx-auto px-4 py-16 md:py-24">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                SEO Blog & Insights
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Expert tips, guides, and strategies to help you grow organic traffic and improve your SEO
              </p>
              
              {/* Search Bar */}
              <div className="relative max-w-2xl mx-auto">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search blog posts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-12 text-lg rounded-full border-2"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="container mx-auto px-4 py-16">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <Card key={i} className="border-border/50 overflow-hidden animate-pulse">
                  <div className="aspect-video w-full bg-muted"></div>
                  <CardContent className="p-6">
                    <div className="h-5 w-20 bg-muted rounded-full mb-3"></div>
                    <div className="h-6 w-full bg-muted rounded mb-2"></div>
                    <div className="h-6 w-4/5 bg-muted rounded mb-4"></div>
                    <div className="h-4 w-full bg-muted rounded mb-2"></div>
                    <div className="h-4 w-5/6 bg-muted rounded mb-4"></div>
                    <div className="flex items-center justify-between">
                      <div className="h-4 w-24 bg-muted rounded"></div>
                      <div className="h-4 w-4 bg-muted rounded"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <p className="text-destructive mb-4">{error}</p>
              <Button onClick={loadPosts} variant="outline">
                Try Again
              </Button>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-xl text-muted-foreground mb-4">
                {searchQuery 
                  ? 'No posts found matching your search' 
                  : 'No blog posts yet'}
              </p>
              {searchQuery && (
                <Button
                  variant="outline"
                  onClick={() => setSearchQuery('')}
                >
                  Clear Search
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <Card
                  key={post.id}
                  className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-border/50 hover:border-primary/30 overflow-hidden"
                  style={{
                    opacity: 0,
                    transform: 'translateY(20px)',
                    animation: `fadeInUp 0.5s ease-out ${Math.min(index * 0.05, 0.3)}s forwards`
                  }}
                  onClick={() => navigate(`/blog/${post.slug}`)}
                  onMouseEnter={() => {
                    // Prefetch blog post data on hover for faster navigation
                    supabase
                      .from('platform_blog_posts')
                      .select('id')
                      .eq('slug', post.slug)
                      .eq('status', 'published')
                      .single()
                      .catch(() => {
                        // Silently fail - prefetch is optional
                      });
                  }}
                >
                  {post.featured_image_url && (
                    <div className="aspect-video w-full overflow-hidden bg-muted">
                      <img
                        src={post.featured_image_url}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <CardContent className="p-6">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {post.primary_keyword && (
                        <Badge variant="secondary" className="text-xs">
                          {post.primary_keyword}
                        </Badge>
                      )}
                    </div>
                    
                    <h2 className="text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors line-clamp-2">
                      {post.title}
                    </h2>
                    
                    {post.excerpt && (
                      <p className="text-muted-foreground mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                    )}
                    
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          <span>{formatDate(post.published_at || post.created_at)}</span>
                        </div>
                        {post.reading_time && (
                          <div className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            <span>{post.reading_time} min read</span>
                          </div>
                        )}
                      </div>
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Blog;

